/**
 * Collection Field Component
 * 
 * Renders a collection of sub-fields that can be added/removed dynamically
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import type { InferredPropertySchema } from '@/types/inferred-schema';
import { DynamicPropertyField } from '../DynamicPropertyField';

interface CollectionFieldProps {
  property: InferredPropertySchema;
  value: any[];
  onChange: (value: any[]) => void;
  error?: string;
}

export function CollectionField({
  property,
  value = [],
  onChange,
  error,
}: CollectionFieldProps) {
  const items = Array.isArray(value) ? value : [];

  const handleAddItem = () => {
    const newItem: any = {};
    
    // Initialize with default values from options or values
    const subFields = property.options || property.values;
    if (subFields) {
      subFields.forEach((option: any) => {
        newItem[option.key] = option.default ?? '';
      });
    }
    
    onChange([...items, newItem]);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
  };

  const handleUpdateItem = (index: number, field: string, fieldValue: any) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      [field]: fieldValue,
    };
    onChange(newItems);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">
          {property.label}
          {property.required && <span className="text-destructive ml-1">*</span>}
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddItem}
          className="h-7 text-xs"
        >
          <Plus className="w-3 h-3 mr-1" />
          Add {property.placeholder || 'Item'}
        </Button>
      </div>

      {property.description && (
        <p className="text-xs text-muted-foreground">{property.description}</p>
      )}

      {items.length === 0 ? (
        <div className="text-xs text-muted-foreground italic p-3 border border-dashed rounded-md text-center">
          No items added yet. Click "Add {property.placeholder || 'Item'}" to get started.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, index) => (
            <div
              key={index}
              className="p-3 border rounded-md bg-muted/30 space-y-3 relative group"
            >
              {/* Item Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">
                    Item {index + 1}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveItem(index)}
                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-3 h-3 text-destructive" />
                </Button>
              </div>

              {/* Item Fields */}
              {(property.options || property.values)?.map((option: any) => {
                const optionSchema: InferredPropertySchema = {
                  key: option.key,
                  label: option.label,
                  type: option.type,
                  required: option.required,
                  default: option.default,
                  placeholder: option.placeholder,
                  description: option.description,
                  options: option.options,
                  typeOptions: option.typeOptions,
                  displayOptions: option.displayOptions,
                };

                return (
                  <div key={option.key} className="space-y-1">
                    <DynamicPropertyField
                      property={optionSchema}
                      value={item[option.key]}
                      onChange={(fieldValue) => handleUpdateItem(index, option.key, fieldValue)}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}
    </div>
  );
}
